import { useEffect, useState, useRef } from "react"
import { useTranslation } from "react-i18next"
import { TabButton } from "../ui/TabButton"
import { ComparableStylePreviewCell, StylePreviewCell } from "./StylePreviewCell"
import clsx from "clsx"

export interface Pair<T> {
	before: T
	after: T
}

export interface StylePreview {
	photoId: string
	url: string | null
}

interface StylePreviewPanelProps {
	isPolarrStyle: boolean
	sampleBeforeAfterPhotos?: Pair<StylePreview>[]
	onPreviewItemClick?: (pairs: Pair<StylePreview>[], index: number) => void
	isFullWidth?: boolean
}

export const StylePreviewPanel = ({
	isPolarrStyle,
	sampleBeforeAfterPhotos = [],
	onPreviewItemClick,
	isFullWidth = false,
}: StylePreviewPanelProps) => {
	const { t } = useTranslation("components")
	const [activeTab, setActiveTab] = useState<"sample" | "your_photos">(
		isPolarrStyle ? "sample" : "your_photos",
	)
	const [showAfter, setShowAfter] = useState<boolean>(true)

	const parentScrollContainer = useRef<HTMLElement | null>(null)
	const headerRef = useRef<HTMLDivElement>(null)
	const photosContainerRef = useRef<HTMLDivElement>(null)
	const panelContainerRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const findScrollContainer = (element: HTMLElement | null): HTMLElement | null => {
			if (!element) return null

			if (element.classList.contains("style-detail-scroll-container")) {
				return element
			}

			const { overflow, overflowY } = window.getComputedStyle(element)
			if (
				overflow === "auto" ||
				overflowY === "auto" ||
				overflow === "scroll" ||
				overflowY === "scroll"
			) {
				return element
			}
			return findScrollContainer(element.parentElement)
		}

		if (panelContainerRef.current) {
			parentScrollContainer.current = findScrollContainer(
				panelContainerRef.current.parentElement as HTMLElement,
			)
		}
	}, [])

	const isHeaderSticky = (): boolean => {
		const container = parentScrollContainer.current
		const header = headerRef.current

		if (!container || !header) {
			return false
		}

		const containerRect = container.getBoundingClientRect()
		const headerRect = header.getBoundingClientRect()
		const stickyOffset = isFullWidth ? 112 : 96

		// If the header's top position equals the sticky position (accounting for some margin of error), it indicates that it is already in sticky mode.
		const headerTopRelativeToViewport = headerRect.top
		const containerTopRelativeToViewport = containerRect.top
		const expectedStickyPosition = containerTopRelativeToViewport + stickyOffset

		const isSticky = Math.abs(headerTopRelativeToViewport - expectedStickyPosition) <= 2 // Allows a 2px margin of error

		return isSticky
	}

	const scrollToFirstPhoto = () => {
		const container = parentScrollContainer.current
		const header = headerRef.current
		const photosContainer = photosContainerRef.current

		if (!container || !header || !photosContainer) {
			return
		}

		const containerRect = container.getBoundingClientRect()

		const stickyOffset = isFullWidth ? 112 : 96

		const firstPhoto = photosContainer.querySelector(".w-full.p-1")
		if (!firstPhoto) {
			return
		}

		const firstPhotoRect = firstPhoto.getBoundingClientRect()

		const firstPhotoCurrentTop = firstPhotoRect.top - containerRect.top

		const headerHeight = header.offsetHeight + 24
		const targetFirstPhotoTop = stickyOffset + headerHeight

		const scrollDelta = firstPhotoCurrentTop - targetFirstPhotoTop
		const targetScrollTop = container.scrollTop + scrollDelta

		container.scrollTo({
			top: Math.max(0, targetScrollTop),
			behavior: "smooth",
		})
	}

	const handleTabChange = (newTab: "sample" | "your_photos") => {
		if (newTab === activeTab) return

		const headerIsSticky = isHeaderSticky()

		setActiveTab(newTab)

		if (headerIsSticky) {
			setTimeout(() => {
				scrollToFirstPhoto()
			}, 100)
		} else {
			// If the header is not sticky, no need to adjust scroll position
		}
	}

	const photoPairs = sampleBeforeAfterPhotos

	const renderPlaceholder = () => {
		return (
			<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
				{Array(6)
					.fill(0)
					.map((_, index) => (
						<div key={index}>
							<StylePreviewCell />
						</div>
					))}
			</div>
		)
	}

	const handleImageClick = (pair: Pair<StylePreview>, index: number) => {
		if (pair.before.url && pair.after.url && onPreviewItemClick) {
			onPreviewItemClick(photoPairs, index)
		}
	}

	const renderStylePreviews = (renderBefore: boolean, source?: Pair<StylePreview>[]) => {
		if (source && source.length > 0) {
			return (
				<div
					className={clsx(
						"w-full gap-2",
						isFullWidth
							? "columns-1 md:columns-2 lg:columns-3 xl:columns-4"
							: "columns-4",
					)}
				>
					{source.map((item, index) => {
						const image = renderBefore ? item.before.url : item.after.url
						const placeholder = renderBefore ? undefined : (image ?? item.before.url)
						return (
							<div key={index} className="mb-2 break-inside-avoid">
								<ComparableStylePreviewCell
									photoId={index}
									source={image}
									index={index}
									param={item}
									placeholder={placeholder}
									onPreviewItemClick={handleImageClick}
								/>
							</div>
						)
					})}
				</div>
			)
		} else {
			return renderPlaceholder()
		}
	}

	const renderPhotos = () => {
		return photoPairs.length > 0 ? renderStylePreviews(!showAfter, photoPairs) : undefined
	}

	const stickyBgColor = isFullWidth ? "bg-base-900" : "bg-base-800"
	const stickyHeaderPosition = isFullWidth ? "top-[calc(5rem-1px)]" : "top-[calc(4rem-1px)]"

	const header = (
		<div
			ref={headerRef}
			className={`sticky ${stickyHeaderPosition} ${stickyBgColor} w-full overflow-x-auto pb-4 pt-3`}
		>
			{isPolarrStyle && (
				<div className="flex min-w-fit items-center justify-between">
					<div className="flex w-1/2 items-center justify-between">
						<TabButton
							isActive={activeTab === "sample"}
							onClick={() => handleTabChange("sample")}
							trackingLabel="style_detail_sample_photos"
						>
							{t("stylePreviewPanel.samplePhotos")}
						</TabButton>
						{photoPairs.length > 0 && (
							<button
								onClick={() => setShowAfter(true)}
								className={clsx(
									"w-20 rounded-l rounded-r-none border-r border-base-800 bg-base-700 px-3 py-1 text-sm font-medium hover:text-base-50",
									showAfter ? "text-base-50" : "text-base-400",
								)}
							>
								{t("stylePreviewPanel.after")}
							</button>
						)}
					</div>

					<div className="flex w-1/2 items-center justify-between">
						{photoPairs.length > 0 && (
							<button
								onClick={() => setShowAfter(false)}
								className={clsx(
									"w-20 rounded-l-none rounded-r bg-base-700 px-3 py-1 text-sm font-medium hover:text-base-50",
									showAfter ? "text-base-400" : "text-base-50",
								)}
							>
								{t("stylePreviewPanel.before")}
							</button>
						)}
					</div>
				</div>
			)}
		</div>
	)

	// Wrap the Preview Panel in a fixed-min-height container to prevent layout jitter.
	return (
		<div ref={panelContainerRef} className="flex min-h-[65vh] flex-1 flex-col">
			{header}
			<div ref={photosContainerRef} className="flex !transition-none">
				{renderPhotos()}
			</div>
		</div>
	)
}
